import { isExceptionalMonthValue } from './exceptionalContributions';

const PAID_CONTRIBUTION_STATUSES = new Set(['completed', 'paid', 'approved']);

/** Période technique frais d'entrée (aligné backend `_entrea_contribution_month_is_adhesion`). */
export function isAdhesionContributionMonth(month) {
  return String(month || '').trim().toLowerCase() === 'adhesion';
}

/** Mois calendaires distincts (YYYY-MM) avec au moins une cotisation mensuelle payée (hors exceptionnelles). */
export function countDistinctPaidRegularMonthsFromRows(rows) {
  const set = new Set();
  for (const c of Array.isArray(rows) ? rows : []) {
    const m = String(c?.month || '').trim();
    if (!/^\d{4}-\d{2}/.test(m)) continue;
    if (isExceptionalMonthValue(m)) continue;
    if (!PAID_CONTRIBUTION_STATUSES.has(String(c.status || '').toLowerCase())) continue;
    set.add(m.slice(0, 7));
  }
  return set.size;
}

/** Montant cotisations hors frais d'adhésion (carte « Cotisations » du tableau de bord membre). */
export function memberRegularContributionsAmount(stats) {
  if (!stats || typeof stats !== 'object') return 0;
  const reg = Number(stats.regular_contributions);
  if (Number.isFinite(reg) && reg >= 0) return reg;
  const total = Number(stats.total_contributions || 0);
  const adh = Number(stats.adhesion_fees ?? stats.membership_fees ?? 0);
  return Math.max(0, total - (Number.isFinite(adh) ? adh : 0));
}

/** Formate cotisations + stats Entrea pour le composant MemberDashboard. */
export function buildMemberDashboardPersonalStats(
  personId,
  contributions,
  stats,
  displayName,
  memberNumber = ''
) {
  const list = Array.isArray(contributions) ? contributions : [];
  let adhesionFromList = 0;
  let regularFromList = 0;
  for (const c of list) {
    if (!PAID_CONTRIBUTION_STATUSES.has(String(c.status || '').toLowerCase())) continue;
    const amt = Number(c.amount || 0);
    if (isAdhesionContributionMonth(c.month)) adhesionFromList += amt;
    else if (isExceptionalMonthValue(c.month)) continue;
    else regularFromList += amt;
  }
  const totalFromList = adhesionFromList + regularFromList;
  const history = [...list]
    .sort((a, b) =>
      String(b.month || b.created_at || '').localeCompare(String(a.month || a.created_at || ''))
    )
    .slice(0, 48)
    .map((c) => ({
      id: c.id,
      _id: c._id,
      month: c.month || c.created_at,
      amount: Number(c.amount || 0),
      status: String(c.status || 'unknown').toLowerCase(),
      paid_at: c.paid_at,
      created_at: c.created_at,
      updated_at: c.updated_at,
    }));
  const pending = new Set(['pending', 'unpaid', 'awaiting', 'draft']);
  const unpaidRows = list.filter(
    (c) =>
      pending.has(String(c.status || '').toLowerCase()) && !isExceptionalMonthValue(String(c?.month || ''))
  );
  const apiTotal = Number(stats?.total_contributions);
  const apiAdh = Number(stats?.adhesion_fees ?? stats?.membership_fees);
  const total_contributions =
    list.length > 0 ? totalFromList : Number.isFinite(apiTotal) ? apiTotal : totalFromList;
  const adhesion_fees =
    list.length > 0
      ? adhesionFromList
      : Number.isFinite(apiAdh)
        ? apiAdh
        : adhesionFromList;
  const regular_contributions =
    list.length > 0
      ? regularFromList
      : Number.isFinite(Number(stats?.regular_contributions))
        ? Number(stats.regular_contributions)
        : Math.max(0, (Number.isFinite(apiTotal) ? apiTotal : total_contributions) - adhesion_fees);
  const mn = String(memberNumber || '').trim();
  const apiPaidMonths = Number(stats?.paid_months);
  const apiUnpaidMonths = Number(stats?.unpaid_months);
  return {
    member_name: displayName || 'Membre',
    member_id: personId,
    person_id: personId,
    ...(mn ? { member_number: mn } : {}),
    ...(Number.isFinite(apiPaidMonths) && apiPaidMonths >= 0 ? { paid_months: apiPaidMonths } : {}),
    ...(Number.isFinite(apiUnpaidMonths) && apiUnpaidMonths >= 0 ? { unpaid_months: apiUnpaidMonths } : {}),
    total_contributions,
    adhesion_fees,
    regular_contributions,
    last_payment: stats?.last_payment_date || stats?.last_payment || null,
    contribution_history: history,
    unpaid_contributions: unpaidRows.map((c) => ({
      month: c.month || c.created_at,
      amount: Number(c.amount || 0),
      status: String(c.status || '').toLowerCase(),
    })),
    pending_requests_count: unpaidRows.length,
    unpaid_count: unpaidRows.length,
    unpaid_total: unpaidRows.reduce((s, c) => s + Number(c.amount || 0), 0),
    pending_requests_total: unpaidRows.reduce((s, c) => s + Number(c.amount || 0), 0),
  };
}

const PAID_FOR_ARREARS = new Set(['completed', 'paid', 'approved']);

function monthKeyFromDate(dt) {
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Estime les mois de retard de cotisation (hors adhésion) pour bandeau / liste membres.
 * Fenêtre = du 1er mois réellement payé (ou adhésion) jusqu’au mois en cours —
 * ne pas inventer des mois avant le début de cotisation (ex. 23 mois fantômes).
 */
export function computeMemberArrearsMonthsForBanner(stats, { paymentDeadlineDay = 15 } = {}) {
  if (!stats || typeof stats !== 'object') return 0;

  const unpaid = Array.isArray(stats.unpaid_contributions) ? stats.unpaid_contributions : [];
  const history = Array.isArray(stats.contribution_history) ? stats.contribution_history : [];

  const paid = new Set();
  const unpaidExisting = new Set();

  for (const c of history) {
    const m = String(c?.month || '').trim();
    if (!m || isAdhesionContributionMonth(m) || isExceptionalMonthValue(m)) continue;
    if (!/^\d{4}-\d{2}/.test(m)) continue;
    const ym = m.slice(0, 7);
    const st = String(c.status || '').toLowerCase().trim();
    if (PAID_FOR_ARREARS.has(st)) paid.add(ym);
  }
  for (const u of unpaid) {
    const m = String(u?.month || '').trim();
    if (!m || isAdhesionContributionMonth(m) || isExceptionalMonthValue(m)) continue;
    if (!/^\d{4}-\d{2}/.test(m)) continue;
    unpaidExisting.add(m.slice(0, 7));
  }

  const paidSorted = [...paid].sort();
  const firstPaidYm = paidSorted[0] || '';
  const now = new Date();
  const startYm = firstPaidYm || '';
  const deadline = Math.min(28, Math.max(1, Number(paymentDeadlineDay) || 15));
  const endOffset = now.getDate() >= deadline ? -1 : -2;
  const endYm = monthKeyFromDate(new Date(now.getFullYear(), now.getMonth() + endOffset, 1));
  if (startYm && endYm < startYm) return 0;

  const keys = new Set();
  const lookback = 36;
  for (let i = 0; i < lookback; i += 1) {
    const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = monthKeyFromDate(dt);
    if (ym > endYm) continue;
    if (startYm && ym < startYm) break;
    if (paid.has(ym)) continue;
    keys.add(ym);
  }
  for (const ym of unpaidExisting) {
    if (ym > endYm) continue;
    if (startYm && ym < startYm) continue;
    if (paid.has(ym)) continue;
    keys.add(ym);
  }

  return Math.min(keys.size, 36);
}

/**
 * Clé de tri chronologique pour une cotisation (plus grand = plus récent).
 * L’adhésion est toujours la plus ancienne ; les mois YYYY-MM suivent le calendrier ;
 * si une date de paiement réelle existe, elle prime.
 */
export function contributionRecencyKey(row) {
  const ts = Date.parse(
    String(row?.paid_at || row?.payment_date || row?.paid_on || row?.updated_at || row?.created_at || '')
  );
  if (Number.isFinite(ts)) return ts;

  const m = String(row?.month || '').trim();
  if (isAdhesionContributionMonth(m)) return Number.MIN_SAFE_INTEGER;
  if (isExceptionalMonthValue(m)) return Number.MIN_SAFE_INTEGER + 1;

  const ym = m.match(/^(\d{4})-(\d{2})/);
  if (ym) {
    const y = Number(ym[1]);
    const mm = Number(ym[2]);
    if (Number.isFinite(y) && Number.isFinite(mm)) {
      return Date.UTC(y, mm - 1, 15);
    }
  }
  return Number.MIN_SAFE_INTEGER + 2;
}

/** Tri décroissant : paiements les plus récents d’abord (adhésion en bas). */
export function compareContributionsByRecencyDesc(a, b) {
  const diff = contributionRecencyKey(b) - contributionRecencyKey(a);
  if (diff !== 0) return diff;
  return String(b?.id || '').localeCompare(String(a?.id || ''));
}
